import { throttle } from 'lodash';
import './styles.scss';

import * as React from 'react';
import classNames from 'classnames';

import { findDOMNode } from 'react-dom';
import TableHeadCell from '../TableHeadCell';
import LoadingContent from '../LoadingContent';

export interface ITableStore {
    tableWidth: number;
    wrapperWidth: number;
    headers: TableHeadCell[];
    headersMapping: ITableHeadCellMapping[];
}

export interface ITableHeadCellMapping {
    header: TableHeadCell;
    index: number;
    width: number;
    priority: number;
}

export interface ITableProps {
    /** className to append */
    className?: string;
    /** Loading */
    loading?: boolean;
}

interface IState {
    tableStore: ITableStore;
}

/**
 * Renders responsive table with configurable columns and rows.
 */
export default class Table extends React.Component<ITableProps, IState> {
    static displayName: string = 'Table';

    private table;

    constructor (props) {
        super(props);
        this.updateDimensions = throttle(this.updateDimensions.bind(this), 200);

        let tableHead = null;
        const headers: TableHeadCell[] = [];

        const findHeaders = (input) => {
            React.Children.map(input, (child) => {
                if (typeof child === 'string' || child === null) {
                    return;
                }
                if (child.type.displayName === 'TableHead') {
                    tableHead = child;
                    return findHeaders(child.props.children);
                }

                if (child.type.displayName === 'TableHeadRow') {
                    return findHeaders(child.props.children);
                }

                if (child.type.displayName === 'TableHeadCell') {
                    headers.push(child);
                }
            });
        };

        findHeaders(this.props.children);

        this.state = {
            tableStore: {
                tableWidth: 0,
                wrapperWidth: 0,
                headers,
                headersMapping: [],
            },
        };
    }

    updateTableWidth () {
        const wrapper = findDOMNode(this) as HTMLElement;
        const table = findDOMNode(this.table) as HTMLElement;

        if (!wrapper || !table) {
            return;
        }

        const wrapperWidth = wrapper.getBoundingClientRect().width;
        if (wrapperWidth !== this.state.tableStore.wrapperWidth) {
            this.setState({
                tableStore: {
                    ...this.state.tableStore,
                    tableWidth: wrapperWidth,
                    wrapperWidth,
                },
            });
        }
    }

    updateHeadersMapping () {
        const { headers } = this.state.tableStore;
        const wrapper = findDOMNode(this) as HTMLElement;
        const table = findDOMNode(this.table) as HTMLElement;
        const CUSTOM_PRIPORITY_OFFSET = 10000;

        if (!wrapper || !table) {
            return;
        }

        // Set wrapper in measuring mode, to see how wide columns and table really are
        wrapper.classList.add('measuring');

        // Map headers for measuring
        const mapping = headers.map((header, index) => {
            // const priority = header.props.priority || (parseInt(uniqueId(), 10) + CUSTOM_PRIPORITY_OFFSET);
            return {
                header,
                index,
                width: 0,
                priority: header.props.priority || CUSTOM_PRIPORITY_OFFSET,
            };
        });

        // Measure only visible columns on correct index
        mapping.filter((n) => {
            return n.header.props.hidden !== true;
        }).map((header, index) => {
            mapping[header.index].width = table.children[0].children[0].children[index].getBoundingClientRect().width + (index === 0 ? 30 : 0);
            if (mapping[header.index].priority === CUSTOM_PRIPORITY_OFFSET) {
                mapping[header.index].priority = CUSTOM_PRIPORITY_OFFSET + mapping[header.index].width;
            }
        });

        // Step out of measuring mode
        wrapper.classList.remove('measuring');

        this.setState({
            tableStore: {
                ...this.state.tableStore,
                headersMapping: mapping,
            },
        });
    }

    updateDimensions () {
        this.updateTableWidth();
    }

    componentDidMount () {
        window.addEventListener('resize', this.updateDimensions);
        this.updateHeadersMapping();
    }

    componentWillUnmount () {
        window.removeEventListener('resize', this.updateDimensions);
    }

    render () {
        return (
            <div className={classNames('responsivetable', {
                loading: this.props.loading,
            })}>
                <table className={classNames('default', this.props.className)} ref={(c) => {
                    this.table = c;
                    this.updateTableWidth();
                }}>
                    {React.Children.map(this.props.children, (child: any) => {
                        if (typeof child === 'string' || typeof child === 'number' || child === null) {
                            return;
                        }
                        if (!child.type.displayName) {
                            // Inside fragment
                            const ret: React.ReactNode[] = [];
                            React.Children.map(child.props.children, (inner) => {
                                if (typeof inner === 'string' || typeof inner === 'number' || inner === null) {
                                    return;
                                }
                                ret.push(React.cloneElement(inner, {
                                    tableStore: this.state.tableStore,
                                }));
                            });
                            return ret;
                        } else {
                            return React.cloneElement(child, { tableStore: this.state.tableStore });
                        }
                    })}
                </table>
                <LoadingContent layout="absolute" />
            </div>
        );
    }
}
